import React, { useEffect, useState } from "react"
import { AlertTriangle, CalendarClock, MoreVertical, Plus } from "lucide-react"
import {
    Avatar,
    Callout,
    FormRow,
    Input,
    Menu,
    MenuItem,
    Pill,
    Select,
    SelectItem,
    Table,
    TableHead,
    TableRow,
    VbButton,
    VbError,
    VbEmpty,
    VbLoading,
} from "@/components/vb"
import ConfirmDialog from "@/components/settings/components/ConfirmDialog"
import { ScheduleFormDialog } from "../components/ScheduleFormDialog"
import DayOfMonthGrid, {
    computeNextRun,
    describeGenerationDay,
    formatRunDate,
    normalizeGenerationDay,
} from "../components/DayOfMonthGrid"
import { projectsInstance, useGetScheduledReports, ScheduleConfig } from "@/services/ProjectsService"
import { useAuth } from "@/context/AuthContext"
import DomainFavicon from "@/components/ui/DomainFavicon"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useCompanyUrl } from "@/hooks/useCompanyUrl"
import { useWorkspace } from "@/context/WorkspaceContext"
import { useReportsStore } from "@/store/store"
import { redirectToStripeWithReturnPath } from "@/lib/stripeNavigation"
import { validateEmail } from "@/lib/utils"

const STRIPE_ONE_TIME_CREDIT_URL = import.meta.env.VITE_STRIPE_ONE_TIME_CREDIT_PURCHASE

/** The backend caps `recipients` at ArrayField(size=10). */
const MAX_RECIPIENTS = 10

/** Stable empty reference — `?? []` inline would be a fresh array every render
 *  and re-fire the selection effect below on each one. */
const NO_SCHEDULES: ScheduleConfig[] = []

const RECIPIENT_COLUMNS = "36px minmax(0,1.5fr) minmax(0,2fr) 36px"
const SCHEDULE_COLUMNS =
    "36px minmax(0,1.6fr) minmax(0,0.9fr) minmax(0,1.4fr) minmax(0,1.5fr) 36px"

/** Recipients are a bare string[] of emails — there is no recipient entity — so
 *  the display name is all we can honestly derive from one. */
const recipientName = (email: string): string => email.split("@")[0] || email

const formatShortDate = (iso: string): string => {
    const parsed = new Date(iso)
    return Number.isNaN(parsed.getTime())
        ? "—"
        : parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
}

const SettingsReport = () => {
    const { token, user, fetchProfile } = useAuth()
    const { workspaces, activeWorkspace, refreshWorkspaces } = useWorkspace()
    const companyUrl = useCompanyUrl()
    const setGenerateDialogRequest = useReportsStore((s) => s.setGenerateDialogRequest)

    // The shell (pages/Settings.tsx) owns the workspace switcher now; this panel
    // just follows the active workspace.
    const selectedWorkspaceId = activeWorkspace ? String(activeWorkspace.id) : null

    const [createDialogOpen, setCreateDialogOpen] = useState(false)
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [scheduleToEdit, setScheduleToEdit] = useState<ScheduleConfig | null>(null)
    const [companyOptions, setCompanyOptions] = useState<
        { value: number; label: string; url?: string | null }[]
    >([])
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
    const [confirmPending, setConfirmPending] = useState(false)
    const [confirmAction, setConfirmAction] = useState<{
        type: 'delete' | 'toggle',
        schedule: ScheduleConfig | null
    }>({ type: 'delete', schedule: null })

    const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null)
    const [selectedDay, setSelectedDay] = useState<number>(normalizeGenerationDay(new Date().getDate()))
    const [savingSchedule, setSavingSchedule] = useState(false)

    const [addRecipientOpen, setAddRecipientOpen] = useState(false)
    const [newRecipient, setNewRecipient] = useState("")
    const [savingRecipients, setSavingRecipients] = useState(false)
    const [recipientToRemove, setRecipientToRemove] = useState<string | null>(null)

    const [runNowOpen, setRunNowOpen] = useState(false)
    const [runningNow, setRunningNow] = useState(false)

    const {
        data: scheduledReports,
        refetch,
        isLoading,
        isError,
        error,
    } = useGetScheduledReports(
        token,
        selectedWorkspaceId !== null,
        selectedWorkspaceId ? Number(selectedWorkspaceId) : null,
    )

    const scheduleErrorMessage =
        error instanceof Error
            ? error.message
            : "Failed to load scheduled reports."

    const schedules = scheduledReports ?? NO_SCHEDULES

    // Derive all available companies from all workspaces and deduplicate.
    // (Deliberately flattened across every workspace, not just the active one —
    // preserved from the pre-AITS-494 panel.)
    useEffect(() => {
        const allCompanies = new Map<number, { value: number; label: string; url?: string | null }>();
        for (const workspace of workspaces) {
            for (const company of workspace.companies) {
                if (!allCompanies.has(company.company_id)) {
                    allCompanies.set(company.company_id, {
                        value: company.company_id,
                        label: company.company_name,
                        url: company.company_url,
                    });
                }
            }
        }
        setCompanyOptions(Array.from(allCompanies.values()));
    }, [workspaces]);

    // Keep the panel's selection pointing at a schedule that still exists.
    useEffect(() => {
        if (!schedules.length) {
            setSelectedScheduleId(null)
            return
        }
        if (!schedules.some((schedule) => schedule.id === selectedScheduleId)) {
            setSelectedScheduleId(schedules[0].id)
        }
    }, [schedules, selectedScheduleId])

    const selectedSchedule =
        schedules.find((schedule) => schedule.id === selectedScheduleId) ?? null

    // The API never returns `generation_day` (write-only) — it only returns
    // `next_generation_ts`, so the day is back-derived from that timestamp.
    const savedDay = selectedSchedule
        ? normalizeGenerationDay(new Date(selectedSchedule.next_generation_ts).getDate())
        : null

    useEffect(() => {
        if (savedDay != null) setSelectedDay(savedDay)
        // Re-seed only when the selected schedule (or its stored day) changes,
        // so picking a cell isn't clobbered on every render.
    }, [selectedSchedule?.id, savedDay])

    const nextRunDate =
        selectedSchedule && selectedDay === savedDay
            ? new Date(selectedSchedule.next_generation_ts)
            : computeNextRun(selectedDay)

    const recipients = selectedSchedule?.recipients ?? []
    const recipientsFull = recipients.length >= MAX_RECIPIENTS
    const trimmedNewRecipient = newRecipient.trim().toLowerCase()
    // Same rules the dialog's addEmail encodes: valid, not a duplicate, under cap.
    const canAddRecipient =
        !!selectedSchedule &&
        !!trimmedNewRecipient &&
        validateEmail(trimmedNewRecipient) &&
        !recipients.includes(trimmedNewRecipient) &&
        !recipientsFull

    // "Run now" has no endpoint of its own — it reuses the shipped re-run path,
    // which needs an existing report to re-run.
    const selectedCompany = activeWorkspace?.companies.find(
        (company) => company.company_id === selectedSchedule?.company_id,
    )
    const runNowReportId = selectedCompany?.lastReportId ?? null
    // Same guard as ReRunReportModal: a run already in flight for this company
    // (any workspace member's) makes "Run now" informational instead of
    // destructive — confirming would discard the run in progress.
    const runNowBlocked = useReportsStore((s) =>
        selectedSchedule != null &&
        Object.values(s.generatingReports).some(
            (r) =>
                String(r.company_id) === String(selectedSchedule.company_id) &&
                (r.status === "in_progress" || r.status === "finalizing"),
        ),
    )
    const totalCredits = user?.total_credits ?? user?.credits ?? 0
    const hasCredits = totalCredits >= 1

    const handleCreateSave = async (data: {
        companies?: string[]
        recipients: string[]
        generationDay: number
    }) => {
        if (!data.companies) return;

        for (const companyId of data.companies) {
            await projectsInstance.createScheduledReport(
                [Number(companyId)],
                data.recipients,
                data.generationDay,
                token
            )
        }
        refetch();
    }

    const handleEditSave = async (data: {
        recipients: string[]
        generationDay: number
        scheduleId?: number
        companyId?: number
    }) => {
        if (!data.scheduleId || !data.companyId) return;

        await projectsInstance.updateScheduledReport(
            data.scheduleId,
            {
                company_id: data.companyId,
                recipients: data.recipients,
                generation_day: data.generationDay
            }
        );
        refetch();
        setScheduleToEdit(null);
    }

    const handleSaveDay = async () => {
        if (!selectedSchedule) return
        setSavingSchedule(true)
        try {
            await projectsInstance.updateScheduledReport(
                selectedSchedule.id,
                {
                    company_id: selectedSchedule.company_id,
                    recipients: selectedSchedule.recipients,
                    generation_day: selectedDay,
                },
                "Report schedule updated",
            )
            await refetch()
        } finally {
            setSavingSchedule(false)
        }
    }

    const handleAddRecipient = async () => {
        if (!selectedSchedule || !canAddRecipient) return
        setSavingRecipients(true)
        try {
            await projectsInstance.updateScheduledReport(
                selectedSchedule.id,
                { recipients: [...recipients, trimmedNewRecipient] },
                "Recipient added",
            )
            setNewRecipient("")
            await refetch()
        } finally {
            setSavingRecipients(false)
        }
    }

    const handleRemoveRecipient = async () => {
        if (!selectedSchedule || !recipientToRemove) return
        setSavingRecipients(true)
        try {
            await projectsInstance.updateScheduledReport(
                selectedSchedule.id,
                { recipients: recipients.filter((email) => email !== recipientToRemove) },
                "Recipient removed",
            )
            setRecipientToRemove(null)
            await refetch()
        } finally {
            setSavingRecipients(false)
        }
    }

    const handleRunNow = async () => {
        if (!runNowReportId) return
        setRunningNow(true)
        try {
            const result = await projectsInstance.reRunReport(runNowReportId, token)

            if (result.success && result.reportId) {
                await fetchProfile()
                setRunNowOpen(false)
                // Stay in Settings; the progress dialog is shell-mounted and
                // opens over it (AITS-540 — it used to need Home mounted to
                // have any data to show).
                setGenerateDialogRequest(result.reportId)
            } else if (result.errorCode === 402) {
                // Server says insufficient credits — refresh the profile and let
                // the dialog re-render into its "no credits" state. Also refresh
                // workspaces: the sidebar credit chip reads the workspace billing
                // account, which fetchProfile does not touch.
                await fetchProfile()
                await refreshWorkspaces()
            } else {
                setRunNowOpen(false)
            }
        } finally {
            setRunningNow(false)
        }
    }

    const handleBuyCredits = () => {
        setRunNowOpen(false)
        if (STRIPE_ONE_TIME_CREDIT_URL && user?.email) {
            redirectToStripeWithReturnPath(STRIPE_ONE_TIME_CREDIT_URL, {
                email: user.email,
                returnPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                clientReferenceId: activeWorkspace ? String(activeWorkspace.id) : null,
            })
        }
    }

    const handleDeleteSchedule = (schedule: ScheduleConfig) => {
        setConfirmAction({ type: 'delete', schedule })
        setConfirmDialogOpen(true)
    }

    const handleToggleStatus = (schedule: ScheduleConfig) => {
        setConfirmAction({ type: 'toggle', schedule })
        setConfirmDialogOpen(true)
    }

    const handleConfirmAction = async () => {
        if (!confirmAction.schedule || confirmPending) return;

        try {
            setConfirmPending(true);
            if (confirmAction.type === 'delete') {
                await projectsInstance.deleteScheduledReport(confirmAction.schedule.id);
            } else if (confirmAction.type === 'toggle') {
                const newStatus = !confirmAction.schedule.is_enabled;
                await projectsInstance.updateScheduledReport(
                    confirmAction.schedule.id,
                    { is_enabled: newStatus },
                    `Schedule ${newStatus ? 'activated' : 'deactivated'} successfully`
                );
            }

            refetch();
            setConfirmDialogOpen(false);
            setConfirmAction({ type: 'delete', schedule: null });
        } finally {
            setConfirmPending(false);
        }
    }

    const handleEditSchedule = (schedule: ScheduleConfig) => {
        setScheduleToEdit(schedule)
        setEditDialogOpen(true)
    }

    return (
        <>
            <section className="vb-card">
                <div className="vb-card-hd">
                    <div className="vb-card-hd__left">
                        <h2>Report schedule</h2>
                        <p className="vb-card-hd__sub">
                            Monthly. Pick which day of the month your report runs.
                        </p>
                    </div>
                    {schedules.length > 1 && selectedSchedule && (
                        <Select
                            value={String(selectedSchedule.id)}
                            onValueChange={(value) => setSelectedScheduleId(Number(value))}
                            aria-label="Company to configure"
                            className="vb-select__trigger--auto"
                        >
                            {schedules.map((schedule) => (
                                <SelectItem key={schedule.id} value={String(schedule.id)}>
                                    {schedule.company_name}
                                </SelectItem>
                            ))}
                        </Select>
                    )}
                </div>

                {isError ? (
                    <VbError description={scheduleErrorMessage} onRetry={() => void refetch()} />
                ) : isLoading ? (
                    <VbLoading rows={2} />
                ) : !selectedSchedule ? (
                    <div className="vb-dashed vb-dashed--row">
                        <div className="vb-dashed__ic">
                            <CalendarClock className="h-[18px] w-[18px]" aria-hidden="true" />
                        </div>
                        <div className="vb-dashed__body">
                            <div className="vb-dashed__t">No report schedule yet</div>
                            <div className="vb-dashed__d">
                                Schedule a company's monthly report and it lands in your recipients'
                                inboxes automatically.
                            </div>
                        </div>
                        <VbButton type="button" onClick={() => setCreateDialogOpen(true)}>
                            Schedule report
                        </VbButton>
                    </div>
                ) : (
                    <>
                        <FormRow
                            label="Day of the month"
                            hint="Your monthly report runs on this day."
                            group
                            help={
                                <>
                                    Selected: <strong>{describeGenerationDay(selectedDay)}</strong> ·
                                    Next report: <strong>{formatRunDate(nextRunDate)}</strong>
                                </>
                            }
                        >
                            <DayOfMonthGrid
                                value={selectedDay}
                                onChange={setSelectedDay}
                                disabled={savingSchedule}
                            />
                        </FormRow>

                        <div className="vb-card-ft">
                            <VbButton
                                type="button"
                                onClick={() => void handleSaveDay()}
                                disabled={savingSchedule || selectedDay === savedDay}
                                title={
                                    selectedDay === savedDay
                                        ? "Pick a different day to save a change"
                                        : undefined
                                }
                            >
                                {savingSchedule ? "Saving…" : "Save schedule"}
                            </VbButton>
                            <VbButton
                                type="button"
                                variant="secondary"
                                onClick={() => setRunNowOpen(true)}
                                disabled={!runNowReportId}
                                title={
                                    runNowReportId
                                        ? undefined
                                        : `${selectedSchedule.company_name} has no report yet — run its first report from the dashboard.`
                                }
                            >
                                Run now
                            </VbButton>
                            <span className="vb-card-ft__note">
                                Changing the day is free. Running now re-runs this month's report and
                                uses 1 credit — you have {totalCredits}.
                            </span>
                        </div>
                    </>
                )}
            </section>

            {selectedSchedule && (
                <section className="vb-card">
                    <div className="vb-card-hd">
                        <div className="vb-card-hd__left">
                            <h2>Who gets the report</h2>
                            <p className="vb-card-hd__sub">
                                Everyone here gets {selectedSchedule.company_name}'s monthly report by
                                email. They don't need an AITS account.
                            </p>
                        </div>
                        <VbButton
                            type="button"
                            onClick={() => setAddRecipientOpen((open) => !open)}
                            disabled={recipientsFull}
                            title={
                                recipientsFull
                                    ? `A schedule can have at most ${MAX_RECIPIENTS} recipients`
                                    : undefined
                            }
                            aria-expanded={addRecipientOpen}
                        >
                            <Plus className="h-[13px] w-[13px]" aria-hidden="true" />
                            Add recipient
                        </VbButton>
                    </div>

                    {recipientsFull && (
                        <Callout
                            tone="warn"
                            icon={<AlertTriangle className="h-[15px] w-[15px]" aria-hidden="true" />}
                        >
                            This schedule is at the <strong>{MAX_RECIPIENTS}-recipient</strong> limit.
                            Remove someone to free up a slot.
                        </Callout>
                    )}

                    {addRecipientOpen && !recipientsFull && (
                        <div className="vb-dashed">
                            <h3 className="vb-dashed__h">Add a recipient</h3>
                            <div className="vb-dashed__row">
                                <Input
                                    type="email"
                                    value={newRecipient}
                                    onChange={(event) => setNewRecipient(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault()
                                            void handleAddRecipient()
                                        }
                                    }}
                                    placeholder="name@company.com"
                                    aria-label="Recipient email"
                                    disabled={savingRecipients}
                                />
                                <VbButton
                                    type="button"
                                    onClick={() => void handleAddRecipient()}
                                    disabled={!canAddRecipient || savingRecipients}
                                >
                                    {savingRecipients ? "Adding…" : "Add"}
                                </VbButton>
                            </div>
                            <p className="vb-dashed__help">
                                They'll get a link to the report in their inbox the day it runs.{" "}
                                {recipients.length}/{MAX_RECIPIENTS} used.
                            </p>
                        </div>
                    )}

                    {recipients.length === 0 ? (
                        <VbEmpty
                            title="No recipients yet"
                            description="Add anyone who should get this report by email."
                        />
                    ) : (
                        <Table
                            columns={RECIPIENT_COLUMNS}
                            minWidth={520}
                            tight
                            framed
                            ariaLabel="Report recipients"
                        >
                            <TableHead>
                                <span />
                                <span>Name</span>
                                <span>Email</span>
                                <span />
                            </TableHead>

                            {recipients.map((email) => {
                                const name = recipientName(email)
                                const isSelf =
                                    !!user?.email && user.email.toLowerCase() === email.toLowerCase()
                                return (
                                    <TableRow key={email}>
                                        <Avatar
                                            size="md"
                                            round
                                            kind="user"
                                            name={name}
                                            decorative
                                        />
                                        <span>
                                            <span className="vb-dt-name">
                                                {name}
                                                {isSelf && <span className="vb-badge-you">You</span>}
                                            </span>
                                        </span>
                                        <span className="vb-dt-last" data-label="Email">
                                            {email}
                                        </span>
                                        <span>
                                            <Menu
                                                align="end"
                                                trigger={
                                                    <button
                                                        type="button"
                                                        className="vb-dt-more"
                                                        aria-label={`Row actions for ${email}`}
                                                    >
                                                        <MoreVertical
                                                            className="h-[14px] w-[14px]"
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                }
                                            >
                                                <MenuItem
                                                    danger
                                                    onSelect={() => setRecipientToRemove(email)}
                                                >
                                                    Remove recipient
                                                </MenuItem>
                                            </Menu>
                                        </span>
                                    </TableRow>
                                )
                            })}
                        </Table>
                    )}
                </section>
            )}

            <section className="vb-card">
                <div className="vb-card-hd">
                    <div className="vb-card-hd__left">
                        <h2>Scheduled reports</h2>
                        <p className="vb-card-hd__sub">
                            One schedule per company. Each run uses one report credit.
                        </p>
                    </div>
                    <VbButton type="button" onClick={() => setCreateDialogOpen(true)}>
                        <Plus className="h-[13px] w-[13px]" aria-hidden="true" />
                        Schedule report
                    </VbButton>
                </div>

                {isError ? (
                    <VbError description={scheduleErrorMessage} onRetry={() => void refetch()} />
                ) : isLoading ? (
                    <VbLoading rows={3} />
                ) : schedules.length === 0 ? (
                    <VbEmpty
                        title="No scheduled reports"
                        description="Schedule a report to have it delivered automatically each month."
                    />
                ) : (
                    <Table
                        columns={SCHEDULE_COLUMNS}
                        minWidth={560}
                        tight
                        framed
                        ariaLabel="Scheduled reports"
                    >
                        <TableHead>
                            <span />
                            <span>Company</span>
                            <span>Status</span>
                            <span>Recipients</span>
                            <span>Occurrence</span>
                            <span />
                        </TableHead>

                        {schedules.map((schedule) => {
                            const [firstRecipient, ...otherRecipients] = schedule.recipients
                            return (
                                <TableRow
                                    key={schedule.id}
                                    selected={schedule.id === selectedScheduleId}
                                >
                                    <DomainFavicon
                                        name={schedule.company_name}
                                        url={companyUrl(schedule.company_id)}
                                        size={30}
                                    />
                                    <span>
                                        <button
                                            type="button"
                                            className="vb-dt-name text-left"
                                            onClick={() => setSelectedScheduleId(schedule.id)}
                                        >
                                            {schedule.company_name}
                                        </button>
                                    </span>
                                    <span data-label="Status">
                                        {schedule.is_enabled ? (
                                            <Pill tone="healthy" quiet>
                                                Active
                                            </Pill>
                                        ) : (
                                            <Pill tone="neutral">Inactive</Pill>
                                        )}
                                    </span>
                                    <span className="vb-dt-last" data-label="Recipients">
                                        {firstRecipient ? (
                                            <>
                                            {/* Grid view: first recipient + a hover-tooltip +N. */}
                                            <span className="vb-rcp-compact inline-flex min-w-0 items-center gap-1.5">
                                                <span className="truncate" title={firstRecipient}>
                                                    {firstRecipient}
                                                </span>
                                                {otherRecipients.length > 0 && (
                                                    <TooltipProvider delayDuration={100}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button
                                                                    type="button"
                                                                    className="shrink-0 cursor-help rounded-vb-pill bg-vb-bg-subtle px-1.5 py-[3px] text-[11px] font-semibold leading-none text-vb-text-2 transition-colors hover:bg-vb-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vb-brand"
                                                                    aria-label={`${otherRecipients.length} more recipient${
                                                                        otherRecipients.length > 1 ? "s" : ""
                                                                    }`}
                                                                >
                                                                    +{otherRecipients.length}
                                                                </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent
                                                                side="top"
                                                                align="start"
                                                                className="max-w-[260px] rounded-vb-md bg-vb-text px-3 py-2 text-vb-on-brand shadow-vb-xl"
                                                            >
                                                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-white/60">
                                                                    Also sent to
                                                                </p>
                                                                <ul className="space-y-0.5">
                                                                    {otherRecipients.map((email) => (
                                                                        <li
                                                                            key={email}
                                                                            className="truncate text-[12px] leading-snug"
                                                                        >
                                                                            {email}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                            </span>
                                            {/* Card view (mobile): no hover, so list every recipient. */}
                                            <span className="vb-rcp-stacked">
                                                {schedule.recipients.map((email) => (
                                                    <span key={email} className="vb-rcp-stacked__item">
                                                        {email}
                                                    </span>
                                                ))}
                                            </span>
                                            </>
                                        ) : (
                                            <span className="vb-dt-last--quiet">no recipients</span>
                                        )}
                                    </span>
                                    <span className="vb-dt-last" data-label="Occurrence">
                                        Monthly · next {formatShortDate(schedule.next_generation_ts)}
                                    </span>
                                    <span>
                                        <Menu
                                            align="end"
                                            trigger={
                                                <button
                                                    type="button"
                                                    className="vb-dt-more"
                                                    aria-label={`Row actions for ${schedule.company_name}`}
                                                >
                                                    <MoreVertical
                                                        className="h-[14px] w-[14px]"
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                            }
                                        >
                                            <MenuItem onSelect={() => handleEditSchedule(schedule)}>
                                                Edit
                                            </MenuItem>
                                            <MenuItem onSelect={() => handleToggleStatus(schedule)}>
                                                {schedule.is_enabled ? 'Deactivate' : 'Activate'}
                                            </MenuItem>
                                            <MenuItem
                                                danger
                                                onSelect={() => handleDeleteSchedule(schedule)}
                                            >
                                                Delete
                                            </MenuItem>
                                        </Menu>
                                    </span>
                                </TableRow>
                            )
                        })}
                    </Table>
                )}
            </section>

            <ScheduleFormDialog
                open={createDialogOpen}
                onOpenChange={setCreateDialogOpen}
                mode="create"
                companyOptions={companyOptions}
                onSave={handleCreateSave}
            />

            <ScheduleFormDialog
                open={editDialogOpen}
                onOpenChange={setEditDialogOpen}
                mode="edit"
                companyOptions={companyOptions}
                scheduleToEdit={scheduleToEdit}
                onSave={handleEditSave}
            />

            <ConfirmDialog
                open={confirmDialogOpen}
                onOpenChange={(open) => {
                    setConfirmDialogOpen(open)
                    if (!open) setConfirmAction({ type: 'delete', schedule: null })
                }}
                title={
                    confirmAction.type === 'delete'
                        ? 'Delete schedule'
                        : confirmAction.schedule?.is_enabled
                            ? 'Deactivate schedule'
                            : 'Activate schedule'
                }
                description={
                    confirmAction.type === 'delete' ? (
                        <>
                            Stop sending{" "}
                            <strong>{confirmAction.schedule?.company_name}</strong>'s monthly report
                            and delete this schedule?
                        </>
                    ) : confirmAction.schedule?.is_enabled ? (
                        <>
                            Pause <strong>{confirmAction.schedule?.company_name}</strong>'s monthly
                            report? The schedule is kept, it just stops running.
                        </>
                    ) : (
                        <>
                            Resume <strong>{confirmAction.schedule?.company_name}</strong>'s monthly
                            report on its scheduled day?
                        </>
                    )
                }
                confirmLabel={
                    confirmAction.type === 'delete'
                        ? 'Delete schedule'
                        : confirmAction.schedule?.is_enabled
                            ? 'Deactivate'
                            : 'Activate'
                }
                destructive={confirmAction.type === 'delete'}
                pending={confirmPending}
                onConfirm={() => void handleConfirmAction()}
            />

            <ConfirmDialog
                open={recipientToRemove !== null}
                onOpenChange={(open) => {
                    if (!open) setRecipientToRemove(null)
                }}
                title="Remove recipient"
                description={
                    <>
                        Stop sending the monthly report to{" "}
                        <strong>{recipientToRemove}</strong>?
                    </>
                }
                confirmLabel="Remove recipient"
                pendingLabel="Removing…"
                destructive
                pending={savingRecipients}
                onConfirm={() => void handleRemoveRecipient()}
            />

            {/* Run now === the shipped re-run path (there is no run-now endpoint),
                so it costs a credit and branches exactly like ReRunReportModal. */}
            <ConfirmDialog
                open={runNowOpen}
                onOpenChange={(open) => {
                    if (!runningNow) setRunNowOpen(open)
                }}
                title={
                    runNowBlocked
                        ? "Your report is already being updated"
                        : hasCredits
                          ? "Run this report now"
                          : "No credits available"
                }
                description={
                    runNowBlocked ? (
                        <>
                            A report for <strong>{selectedSchedule?.company_name}</strong> is being
                            generated right now. You can run it again once this one finishes,
                            usually within a few minutes.
                        </>
                    ) : hasCredits ? (
                        <>
                            This re-checks <strong>{selectedSchedule?.company_name}</strong> against
                            every AI Trust Signal and updates this month's report. It takes a few
                            minutes and uses <strong>1 credit</strong>.
                        </>
                    ) : (
                        "You've used all your credits. Purchase additional credits to run this report now."
                    )
                }
                confirmLabel={runNowBlocked ? "Got it" : hasCredits ? "Run now" : "Buy credits"}
                pendingLabel="Starting…"
                pending={runningNow}
                onConfirm={() =>
                    runNowBlocked
                        ? setRunNowOpen(false)
                        : hasCredits
                          ? void handleRunNow()
                          : handleBuyCredits()
                }
            >
                {runNowBlocked ? null : hasCredits ? (
                    <p className="text-[12px] leading-relaxed text-vb-text-3">
                        <strong>Note:</strong> Re-running within the same month does not guarantee
                        score changes. Some AI Trust Signals take longer for LLMs to recognise.
                    </p>
                ) : (
                    <Callout
                        tone="warn"
                        icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
                    >
                        Running a report costs <strong>1 credit</strong> and you have{" "}
                        <strong>{totalCredits}</strong>.
                    </Callout>
                )}
            </ConfirmDialog>
        </>
    )
}

export default SettingsReport
